import axiosInstance from '@/lib/axios';
import { LoginFormData } from '../schema/loginSchema';
import { AuthResponse } from '../types/auth.types';

interface OtpRequestResponse {
  statusCode: number;
  data: {
    message: string;
    expiresIn: number;
    email: string;
  };
  message: string;
}

interface OtpVerifyResponse extends AuthResponse {}

export const authApi = {
  /**
   * Login user with email and password
   */
  login: async (data: LoginFormData): Promise<AuthResponse> => {
    const response = await axiosInstance.post<AuthResponse>('/auth/login', data);
    return response.data;
  },

  /**
   * Validate credentials and request OTP code for login
   * New flow: validates email + password first, then sends OTP
   */
  requestOtp: async (email: string, password: string): Promise<OtpRequestResponse> => {
    const response = await axiosInstance.post<OtpRequestResponse>('/auth/otp/request', { email, password });
    return response.data;
  },

  /**
   * Verify OTP code and complete login
   */
  verifyOtp: async (email: string, otpCode: string): Promise<OtpVerifyResponse> => {
    const response = await axiosInstance.post<OtpVerifyResponse>('/auth/otp/verify', { email, otpCode });
    return response.data;
  },

  /**
   * Resend OTP code
   */
  resendOtp: async (email: string): Promise<OtpRequestResponse> => {
    const response = await axiosInstance.post<OtpRequestResponse>('/auth/otp/resend', { email });
    return response.data;
  },

  /**
   * Logout user and clear tokens
   */
  logout: async (): Promise<void> => {
    try {
      await axiosInstance.post('/auth/logout');
    } finally {
      // Always clear tokens, even if API call fails
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
    }
  },

  /**
   * Refresh access token using refresh token
   */
  refreshToken: async (refreshToken: string): Promise<{ accessToken: string }> => {
    const response = await axiosInstance.post('/auth/refresh', { refreshToken });
    return response.data;
  },

  /**
   * Get current user profile
   */
  getCurrentUser: async () => {
    const response = await axiosInstance.get('/auth/me');
    return response.data;
  },

  /**
   * Verify if token is valid
   */
  verifyToken: async (): Promise<boolean> => {
    try {
      await axiosInstance.get('/auth/verify');
      return true;
    } catch {
      return false;
    }
  },

  /**
   * Request password reset email
   */
  forgotPassword: async (email: string): Promise<void> => {
    await axiosInstance.post('/auth/forgot-password', { email });
  },

  /**
   * Reset password with token
   */
  resetPassword: async (token: string, password: string): Promise<void> => {
    await axiosInstance.post('/auth/reset-password', { token, password });
  },
};